import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Filters, DataTable, Page, Card, Link, Badge, Button, Tooltip } from '@shopify/polaris';
import { useUrl } from '../../shared/util/hanldesave';
import { getDocPdfLinkForListView, getTableFooter, getModalButton, fetchTableData, getUserOrg, getRequest, filtersToApiUrl } from '../../shared/components/functions.jsx';
import { applyFilters, addFilters, handleFilterChange, removeFilter, handleFiltersClearAll, getUserOptions } from '../../shared/components/listFilter.jsx';
import ExcelExporterModal from '../../shared/components/modals/excelExporterModal.jsx';
import { emailSentBadge } from '../../shared/components/badges/badges.jsx';
import QuickViewModal from '../../shared/components/modals/quickViewModal.jsx';
import { useDebounce } from '../../shared/components/inputs/useDebounce.js';
import { useAuthStore, useLookupStore } from '../../context/useStore.js';
import SharePointPdfPreviewButton from '../../shared/components/SharePointPdfPreviewButton.jsx';
import {
  INQUIRY_STATUS_IDS,
  MANUAL_INQUIRY_STATUS_IDS,
  deriveInquiryStatusId,
  formatMoment,
  getInquiryStatusLabel,
  getInquiryStatusTone,
  parseLinkedPurchaseOrders
} from '../../shared/inquiryStatus.js';

// Draft / Sent / Quotation received can be set here; Ordered follows the linked purchase order (server derives it)
function InlineStatusCell({ value, label, options, saving = false, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const editable = MANUAL_INQUIRY_STATUS_IDS.includes(Number(value));

  useEffect(() => {
    if (!editing) setDraft(value || '');
  }, [value, editing]);

  const cancel = () => {
    setDraft(value || '');
    setEditing(false);
  };

  const commit = async (nextValue) => {
    setEditing(false);
    const parsedValue = nextValue ? Number(nextValue) : null;
    if (!parsedValue || parsedValue === value) return;
    await onSave(parsedValue);
  };

  if (editing) {
    return (
      <select
        autoFocus
        value={draft}
        disabled={saving}
        onChange={(e) => {
          const nextValue = e.target.value;
          setDraft(nextValue);
          void commit(nextValue);
        }}
        onBlur={cancel}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
          }
        }}
        style={{
          width: '170px',
          height: '28px',
          fontSize: '12px',
          lineHeight: '16px',
          padding: '2px 6px',
          border: '1px solid #c9cccf',
          borderRadius: '6px',
          boxSizing: 'border-box',
          background: '#fff'
        }}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  const badge = <Badge tone={getInquiryStatusTone(value)}>{saving ? 'Saving...' : label}</Badge>;

  if (!editable) {
    return <Tooltip content="Follows the linked purchase order">{badge}</Tooltip>;
  }

  return (
    <button
      type="button"
      disabled={saving}
      onClick={() => setEditing(true)}
      style={{
        border: 'none',
        background: 'transparent',
        padding: 0,
        margin: 0,
        cursor: saving ? 'default' : 'pointer'
      }}
      title="Edit status"
    >
      {badge}
    </button>
  );
}

export default function InquiriesPage() {
  const url = useUrl();
  const navigate = useNavigate();
  const users = useLookupStore((state) => state.users);
  const organisations = useLookupStore((state) => state.organisations);
  const { user } = useAuthStore();
  const userOrg = getUserOrg(organisations, user.ORGANISATION_NAME);
  const filteredUsers = getUserOptions(users, userOrg);

  const [items, setItems] = useState([]);
  const [inquiryStatuses, setInquiryStatuses] = useState([]);
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const [savingCells, setSavingCells] = useState({});
  // Polaris <Filters> only shows pills for filters applied through its own popover UI; bump this key
  // to remount it after setting filters programmatically so it re-reads the applied filters.
  const [filtersRemountKey, setFiltersRemountKey] = useState(0);
  const [filters, setFilters] = useState({
    q: { type: 'text', name: 'Q', value: url.get('q') || '' },
    supplier: { type: 'text', name: 'Supplier', value: url.get('supplier') || '' },
    dateFrom: { type: 'date', name: 'Date from', value: url.get('dateFrom') || '' },
    dateTo: { type: 'date', name: 'Date to', value: url.get('dateTo') || '' },
    customer: { type: 'text', name: 'Customer', value: url.get('customer') || '' },
    reference: { type: 'text', name: 'Reference', value: url.get('reference') || '' },
    machineType: { type: 'text', name: 'Machine type', value: url.get('machineType') || '' },
    status: { type: 'multiple', name: 'Status', value: url.get('status') ? JSON.parse(url.get('status')) : null, choices: [] },
    mail: { type: 'status', name: 'PDF emailed', value: url.get('mail') || 'all' },
    owner: { type: 'multiple', name: 'Owner', value: url.get('owner') ? JSON.parse(url.get('owner')) : null, choices: [] }
  });
  const statusChoices = inquiryStatuses.map((status) => ({ label: `${status.Status}`, value: `${status.InquiryStatusID}` }));
  const manualStatusOptions = inquiryStatuses
    .filter((status) => MANUAL_INQUIRY_STATUS_IDS.includes(Number(status.InquiryStatusID)))
    .map((status) => ({ label: `${status.Status}`, value: `${status.InquiryStatusID}` }));
  const displayFilters = {
    ...filters,
    status: { ...filters.status, choices: statusChoices },
    owner: { ...filters.owner, choices: filteredUsers }
  };

  const fetchData = async (abortController) => await fetchTableData(page, '/api/inquiries', setItems, filters, abortController, setFilterLoading);
  const debouncedFetchData = useDebounce(fetchData);

  useEffect(() => {
    const fetchStatuses = async () => {
      const statuses = await getRequest('/api/inquiries/statuses');
      if (Array.isArray(statuses)) setInquiryStatuses(statuses);
    };
    fetchStatuses();
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    const fetchDataWithAbort = async () => debouncedFetchData(abortController);
    fetchDataWithAbort();
    return () => abortController.abort(); // Cleanup, abort controller when page/filters change
  }, [page, filters]);

  const updateInquiryStatus = async (inquiryId, nextStatusId) => {
    const savingKey = `${inquiryId}:status`;
    setSavingCells((current) => ({ ...current, [savingKey]: true }));

    try {
      const response = await fetch(`/api/inquiries/${inquiryId}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ statusId: nextStatusId })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.msg || 'Failed to update the inquiry status.');
      }

      setItems((currentItems) =>
        currentItems.map((item) => (item.PrijsaanvraagID === inquiryId ? { ...item, ...data, inquiry_status_name: data.InquiryStatusName, linked_purchase_orders: item.linked_purchase_orders } : item))
      );
    } catch (error) {
      alert(error.message || 'Failed to update the inquiry status.');
    } finally {
      setSavingCells((current) => ({ ...current, [savingKey]: false }));
    }
  };

  const applyPresetFilters = (updates) => {
    const nextFilters = { ...filters };
    for (const [key, value] of Object.entries(updates)) {
      nextFilters[key] = { ...nextFilters[key], value };
    }

    setFilters(nextFilters);
    setFiltersRemountKey((key) => key + 1);
    navigate({ pathname: window.location.pathname, search: `${filtersToApiUrl(nextFilters)}` }, { replace: true });
    setPage(1);
  };

  const rows = Array.isArray(items)
    ? items.map((item) => {
        const statusId = deriveInquiryStatusId(item);
        const statusLabel = getInquiryStatusLabel(statusId, inquiryStatuses);
        const purchaseOrders = parseLinkedPurchaseOrders(item.linked_purchase_orders);
        return [
          <Link removeUnderline url={'/inquiries/' + item.PrijsaanvraagID}>
            {item.Prijsaanvraagnummer}
          </Link>,
          getModalButton(item.PrijsaanvraagID, item.Prijsaanvraagnummer, setModalOpen, setSelectedItem),
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
            {getDocPdfLinkForListView(item, 'price_request', item.supplier_number, item.Prijsaanvraagnummer)}
            <SharePointPdfPreviewButton
              companyNumber={item.supplier_number}
              documentTypeId={14}
              itemNumber={item.Prijsaanvraagnummer}
              fileName={item.SupplierQuotationFilename}
              buttonLabel="Q-PDF"
              tooltipContent={`Supplier quotation${item.SupplierQuotationUploadedAt ? ` (${formatMoment(item.SupplierQuotationUploadedAt)})` : ''}`}
              modalTitle={`Supplier quotation ${item.Prijsaanvraagnummer}`}
            />
          </div>,
          <Link removeUnderline url={'/companies/' + item.LeverancierID}>
            {String(item.supplier_name).substring(0, 30)}
          </Link>,
          String(item.Datum).substring(0, 10),
          item.KlantID ? (
            <Link removeUnderline url={'/companies/' + item.KlantID}>
              {String(item.customer_name).substring(0, 30)}
            </Link>
          ) : (
            ''
          ),
          item.RefKlant ? item.RefKlant : '',
          item.RefKlantDatum ? String(item.RefKlantDatum).substring(0, 10) : '',
          item.machinetype,
          item.Afkorting ? <Badge>{item.Afkorting}</Badge> : '',
          <InlineStatusCell
            value={statusId}
            label={statusLabel}
            options={manualStatusOptions}
            saving={Boolean(savingCells[`${item.PrijsaanvraagID}:status`])}
            onSave={(nextValue) => updateInquiryStatus(item.PrijsaanvraagID, nextValue)}
          />,
          emailSentBadge(item.Verstuurd),
          purchaseOrders.length ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
              {purchaseOrders.map((order) => (
                <Tooltip key={order.id} content={`Purchase order ${order.number || order.id}${order.date ? ` · ${formatMoment(order.date)}` : ''}`}>
                  <Link removeUnderline url={'/purchases/' + order.id}>
                    {order.number || `#${order.id}`}
                  </Link>
                </Tooltip>
              ))}
            </div>
          ) : (
            ''
          )
        ];
      })
    : [];

  return (
    <Page title="Inquiries" fullWidth>
      <QuickViewModal modalOpen={modalOpen} setModalOpen={setModalOpen} id={selectedItem.id} type="default" source="inquiries" title={`Inquiry #${selectedItem.number}`} />
      <Card>
        <Filters
          key={filtersRemountKey}
          queryValue={filters.q.value}
          filters={addFilters(displayFilters, setFilters, navigate, setPage)}
          appliedFilters={applyFilters(displayFilters, setFilters, navigate, setPage)}
          loading={filterLoading}
          onQueryChange={(e) => handleFilterChange(e, displayFilters, setFilters, 'q', navigate, setPage)}
          onQueryClear={() => removeFilter('q', displayFilters, setFilters, navigate, setPage)}
          autoComplete="off"
          queryPlaceholder="Filter inquiries on number, reference, supplier name, machine type and delivery term"
          onClearAll={() => handleFiltersClearAll(displayFilters, setFilters, navigate, setPage)}
        >
          <Button size="slim" onClick={() => applyPresetFilters({ status: [String(INQUIRY_STATUS_IDS.SENT)] })}>
            Awaiting quotation
          </Button>
          <Button size="slim" onClick={() => applyPresetFilters({ status: [String(INQUIRY_STATUS_IDS.QUOTATION_RECEIVED)] })}>
            Quotation received
          </Button>
          <ExcelExporterModal filters={filters} setFilters={setFilters} page={page} setPage={setPage} />
        </Filters>
        <DataTable
          verticalAlign="middle"
          stickyHeader
          increasedTableDensity
          columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text']}
          headings={[
            <b>Number</b>,
            <b>View</b>,
            <b>PDF</b>,
            <b>Supplier</b>,
            <b>Date</b>,
            <b>Customer</b>,
            <b>Reference</b>,
            <b>Customer date</b>,
            <b>Machine type</b>,
            <b>Owner</b>,
            <b>Status</b>,
            <b>PDF emailed</b>,
            <b>Purchase order</b>
          ]}
          rows={rows}
          footerContent={getTableFooter(rows, page, setPage, 'inquiries')}
        />
      </Card>
    </Page>
  );
}
